import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useOrg } from '../app/OrgProvider';
import { AppLink } from '../components/Nav';
import {
  Badge,
  Banner,
  Button,
  CopyButton,
  EmptyState,
  ErrorText,
  Input,
  Loading,
  PageHeader,
  Spinner,
  cx,
} from '../components/ui';
import { errorMessage } from '../lib/api';
import { timecode } from '../lib/format';
import type { BrandingSettings, BrandingSlateRef, VideoSummary } from '../lib/types';
import { qk } from '../queries/keys';
import {
  checkCustomDomain,
  domainInFlight,
  domainLabel,
  fetchBranding,
  logoProblem,
  removeCustomDomain,
  removeLogo,
  updateBranding,
  uploadLogo,
  type LogoKind,
} from '../queries/branding';
import { setCustomDomain } from '../queries/branding';
import { fetchLibrary } from '../queries/videos';
import { SettingsTabs } from './SettingsPage';

/**
 * Settings › Branding. The team's face on everything their viewers see.
 *
 * Every section here writes through the same endpoint and is handed the whole
 * settings object back, so the page never merges a partial answer into what it
 * was already showing. `mayManage` disables the controls for a plain member
 * rather than hiding them: they can see what the team's branding is, which
 * they can see on every share page anyway.
 */

/** The page is one card per thing a team can set. */
function Section({
  title,
  description,
  children,
  aside,
}: {
  title: string;
  description: ReactNode;
  children: ReactNode;
  aside?: ReactNode;
}) {
  return (
    <section className="card p-4 sm:p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-h4 text-chalk">{title}</h2>
          <p className="mt-1 max-w-xl text-caption text-chalk-dim">{description}</p>
        </div>
        {aside ? <div className="shrink-0">{aside}</div> : null}
      </div>
      <div className="mt-4">{children}</div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Logos
// ---------------------------------------------------------------------------

/**
 * One logo slot. The square one is used anywhere small and square (the tab's
 * icon, a link preview); the wide one stands where the Slate wordmark does.
 */
function LogoSlot({
  kind,
  url,
  disabled,
  onPick,
  onClear,
  busy,
}: {
  kind: LogoKind;
  url: string | null;
  disabled: boolean;
  onPick: (file: File) => void;
  onClear: () => void;
  busy: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const square = kind === 'square';

  return (
    <div className="space-y-2">
      <p className="text-caption font-medium text-chalk-dim">{square ? 'Square' : 'Landscape'}</p>
      <div
        className={cx(
          'flex items-center justify-center overflow-hidden rounded-md border border-dashed border-hairline bg-slate-900',
          square ? 'h-28 w-28' : 'h-28 w-full max-w-xs',
        )}
      >
        {busy ? (
          <Spinner className="text-chalk-faint" />
        ) : url ? (
          <img src={url} alt={`${square ? 'Square' : 'Landscape'} logo`} className="max-h-full max-w-full object-contain p-2" />
        ) : (
          <span className="px-3 text-center text-caption text-chalk-faint">
            {square ? '1:1, 512px' : 'Wide, about 4:1'}
          </span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          disabled={disabled || busy}
          onClick={() => input.current?.click()}
        >
          {url ? 'Replace' : 'Upload'}
        </Button>
        {url ? (
          <button
            type="button"
            className="text-caption text-chalk-dim hover:text-chalk disabled:opacity-40"
            disabled={disabled || busy}
            onClick={onClear}
          >
            Remove
          </button>
        ) : null}
      </div>
      <input
        ref={input}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/svg+xml"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          // Same input can pick the same file twice in a row; clearing the
          // value is what makes the second time fire a change at all.
          event.target.value = '';
          if (!file) return;
          const why = logoProblem(file);
          setProblem(why);
          if (!why) onPick(file);
        }}
      />
      <ErrorText>{problem}</ErrorText>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Slates
// ---------------------------------------------------------------------------

/**
 * Picking a recording to play before or after every other one.
 *
 * The list is the team's own library, which is where these come from: a slate
 * is a recording somebody already made and shared, not a separate kind of
 * thing to upload.
 */
function SlatePicker({
  label,
  description,
  current,
  choices,
  loading,
  disabled,
  onChoose,
  onClear,
}: {
  label: string;
  description: string;
  current: BrandingSlateRef | null;
  choices: VideoSummary[];
  loading: boolean;
  disabled: boolean;
  onChoose: (videoId: string) => void;
  onClear: () => void;
}) {
  const [picking, setPicking] = useState(false);

  return (
    <div className="rounded-md border border-hairline bg-slate-900/40 p-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-body text-chalk">{label}</p>
          <p className="mt-0.5 text-caption text-chalk-faint">{description}</p>
        </div>
        {current && !picking ? (
          <div className="flex shrink-0 items-center gap-2">
            <Button size="sm" disabled={disabled} onClick={() => setPicking(true)}>
              Change
            </Button>
            <button
              type="button"
              className="text-caption text-chalk-dim hover:text-chalk disabled:opacity-40"
              disabled={disabled}
              onClick={onClear}
            >
              Remove
            </button>
          </div>
        ) : !picking ? (
          <Button size="sm" disabled={disabled} onClick={() => setPicking(true)}>
            Choose
          </Button>
        ) : (
          <button
            type="button"
            className="shrink-0 text-caption text-chalk-dim hover:text-chalk"
            onClick={() => setPicking(false)}
          >
            Cancel
          </button>
        )}
      </div>

      {current && !picking ? (
        <div className="mt-3 flex items-center gap-3">
          <div className="h-12 w-20 shrink-0 overflow-hidden rounded bg-slate-900">
            {current.posterUrl ? (
              <img src={current.posterUrl} alt="" className="h-full w-full object-cover" />
            ) : null}
          </div>
          <div className="min-w-0">
            <AppLink to={`/v/${current.shareId}`} className="block truncate text-body text-chalk hover:underline">
              {current.title}
            </AppLink>
            <p className="text-caption text-chalk-faint">
              {current.durationSec ? <span className="tnum font-mono">{timecode(current.durationSec)}</span> : null}
              {!current.playable ? (
                <span className="ml-2 text-caution">Not ready, so it is skipped</span>
              ) : null}
            </p>
          </div>
        </div>
      ) : null}

      {picking ? (
        loading ? (
          <div className="mt-3">
            <Loading label="Loading the team’s recordings" />
          </div>
        ) : choices.length === 0 ? (
          <p className="mt-3 text-caption text-chalk-faint">
            Nothing in the team library yet. Record the slate first, then come back and choose it here.
          </p>
        ) : (
          <ul className="mt-3 max-h-64 space-y-1 overflow-y-auto pr-1">
            {choices.map((video) => (
              <li key={video.id}>
                <button
                  type="button"
                  className="flex w-full items-center gap-3 rounded p-1.5 text-left hover:bg-slate-800"
                  onClick={() => {
                    onChoose(video.id);
                    setPicking(false);
                  }}
                >
                  <span className="h-10 w-16 shrink-0 overflow-hidden rounded bg-slate-900">
                    {video.posterUrl ? <img src={video.posterUrl} alt="" className="h-full w-full object-cover" /> : null}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-body text-chalk">{video.title}</span>
                    {video.durationSec ? (
                      <span className="text-caption tnum font-mono text-chalk-faint">{timecode(video.durationSec)}</span>
                    ) : null}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The team's own domain
// ---------------------------------------------------------------------------

/**
 * Three stages, one record at a time. Which record is shown follows the
 * status, because showing both at once means the second is stale for as long
 * as the first takes.
 */
function DomainSection({
  branding,
  orgId,
  disabled,
  onSettled,
}: {
  branding: BrandingSettings;
  orgId: string;
  disabled: boolean;
  onSettled: (next: BrandingSettings) => void;
}) {
  const domain = branding.domain;
  const [host, setHost] = useState('');
  const label = domainLabel(domain.status);

  const claim = useMutation({
    mutationFn: () => setCustomDomain(orgId, host.trim()),
    onSuccess: onSettled,
  });
  const check = useMutation({ mutationFn: () => checkCustomDomain(orgId), onSuccess: onSettled });
  const drop = useMutation({
    mutationFn: () => removeCustomDomain(orgId),
    onSuccess: (next) => {
      setHost('');
      onSettled(next);
    },
  });

  // While a domain is in flight the page asks again every fifteen seconds.
  // The API throttles what it actually asks AWS, so this is cheap, and it is
  // what makes the walk finish without anybody pressing anything.
  useEffect(() => {
    if (!domainInFlight(domain.status)) return;
    const timer = window.setInterval(() => {
      if (!check.isPending) check.mutate();
    }, 15_000);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain.status, check.isPending]);

  if (!domain.available) {
    return (
      <p className="text-caption text-chalk-faint">
        Custom domains are not available on this deployment.
      </p>
    );
  }

  if (!domain.host) {
    return (
      <form
        className="max-w-md space-y-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (host.trim()) claim.mutate();
        }}
      >
        <Input
          label="Domain"
          placeholder="videos.acme.com"
          value={host}
          disabled={disabled}
          autoComplete="off"
          spellCheck={false}
          onChange={(event) => setHost(event.target.value)}
          hint="A subdomain of a domain you own. You will add one DNS record to prove it is yours, and a second to point it at us."
        />
        <Button type="submit" variant="primary" size="sm" busy={claim.isPending} disabled={disabled || !host.trim()}>
          Add domain
        </Button>
        {claim.isError ? <ErrorText>{errorMessage(claim.error)}</ErrorText> : null}
      </form>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <span className="font-mono text-body text-chalk">{domain.host}</span>
        <Badge tone={label.tone}>{label.title}</Badge>
        {domainInFlight(domain.status) ? <Spinner size={14} className="text-chalk-faint" /> : null}
      </div>

      {domain.status === 'active' ? (
        <Banner tone="go" title="Links use your domain">
          New links look like <span className="font-mono">https://{domain.host}/v/…</span>. Links already sent on the
          Slate address keep working.
        </Banner>
      ) : null}

      {domain.error ? <Banner tone="caution" title="Last check">{domain.error}</Banner> : null}

      {domain.record ? (
        <div className="rounded-md border border-hairline bg-slate-900/40 p-3">
          <p className="text-caption text-chalk-dim">
            {domain.status === 'verifying'
              ? 'Add this record at your DNS provider. It proves the domain is yours so a certificate can be issued; it usually takes a few minutes once the record is live.'
              : 'Now point the domain itself at us with this record. Links switch over on their own once it resolves.'}
          </p>
          <dl className="mt-3 space-y-2">
            {[
              { label: 'Type', value: domain.record.type },
              { label: 'Name', value: domain.record.name },
              { label: 'Value', value: domain.record.value },
            ].map((row) => (
              <div key={row.label} className="flex items-start gap-3">
                <dt className="w-14 shrink-0 pt-1 text-caption text-chalk-faint">{row.label}</dt>
                <dd className="flex min-w-0 flex-1 items-start gap-2">
                  <code className="min-w-0 flex-1 break-all rounded bg-slate-950 px-2 py-1 font-mono text-caption text-chalk">
                    {row.value}
                  </code>
                  <CopyButton value={row.value} label="Copy" copiedLabel="Copied" size="sm" />
                </dd>
              </div>
            ))}
          </dl>
        </div>
      ) : null}

      <div className="flex items-center gap-3">
        <Button size="sm" busy={check.isPending} disabled={disabled} onClick={() => check.mutate()}>
          Check again
        </Button>
        <button
          type="button"
          className="text-caption text-chalk-dim hover:text-chalk disabled:opacity-40"
          disabled={disabled || drop.isPending}
          onClick={() => drop.mutate()}
        >
          {drop.isPending ? 'Removing…' : 'Remove domain'}
        </button>
      </div>
      {check.isError ? <ErrorText>{errorMessage(check.error)}</ErrorText> : null}
      {drop.isError ? <ErrorText>{errorMessage(drop.error)}</ErrorText> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------

export function BrandingPage() {
  const { org, loading: orgLoading } = useOrg();
  const client = useQueryClient();
  const orgId = org?.id ?? '';

  const query = useQuery({
    queryKey: qk.branding(orgId),
    queryFn: () => fetchBranding(orgId),
    enabled: orgId !== '',
  });
  const branding = query.data ?? null;
  const settled = (next: BrandingSettings) => client.setQueryData(qk.branding(orgId), next);

  // The team's own recordings, for the slate pickers. Only fetched once
  // somebody opens this page, and only the team's shelf is offered.
  const library = useQuery({
    queryKey: qk.videos('branding-slates'),
    queryFn: () => fetchLibrary({ limit: 100 }),
    enabled: orgId !== '',
    staleTime: 60_000,
  });
  const slateChoices = (library.data?.videos ?? []).filter(
    (video) => video.orgId === orgId && video.status === 'ready' && video.kind !== 'audio' && !video.locked,
  );

  const [color, setColor] = useState('');
  useEffect(() => {
    if (branding) setColor(branding.brandColor ?? '');
  }, [branding]);

  const save = useMutation({
    mutationFn: (body: Parameters<typeof updateBranding>[1]) => updateBranding(orgId, body),
    onSuccess: settled,
  });
  const logo = useMutation({
    mutationFn: ({ kind, file }: { kind: LogoKind; file: File }) => uploadLogo(orgId, kind, file),
    onSuccess: settled,
  });
  const clearLogo = useMutation({
    mutationFn: (kind: LogoKind) => removeLogo(orgId, kind),
    onSuccess: settled,
  });

  if (orgLoading || (orgId !== '' && query.isLoading)) {
    return (
      <>
        <PageHeader title="Settings" />
        <SettingsTabs active="branding" />
        <Loading label="Loading your branding" />
      </>
    );
  }

  if (!org) {
    return (
      <>
        <PageHeader title="Settings" subtitle="Your account, your plan and your team." />
        <SettingsTabs active="branding" />
        <EmptyState title="Branding belongs to a team">
          Your logo, colour and slates go on everything the team shares, so there has to be a team first.
          <div className="mt-4">
            <AppLink to="/settings/subscription">
              <Button variant="primary">Start a team</Button>
            </AppLink>
          </div>
        </EmptyState>
      </>
    );
  }

  if (query.isError || !branding) {
    return (
      <>
        <PageHeader title="Settings" />
        <SettingsTabs active="branding" />
        <Banner tone="danger" title="That did not load">{errorMessage(query.error)}</Banner>
      </>
    );
  }

  const locked = !branding.mayManage || !branding.included;
  const colorValid = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(color.trim());
  const colorDirty = (branding.brandColor ?? '') !== (color.trim() ? color.trim().toLowerCase() : '');

  return (
    <>
      <PageHeader
        title="Settings"
        subtitle={`How ${org.name} looks to everyone you share a recording with.`}
      />
      <SettingsTabs active="branding" />

      <div className="space-y-6">
        {!branding.included ? (
          <Banner
            tone="caution"
            title="Custom branding comes with the team plan"
            action={
              <AppLink to="/settings/subscription">
                <Button size="sm" variant="primary">
                  See the plan
                </Button>
              </AppLink>
            }
          >
            Everything set here is kept exactly as it is. Share pages show Slate's own look until the plan is running
            again, and yours the moment it is.
          </Banner>
        ) : !branding.mayManage ? (
          <Banner tone="info" title="Only admins and owners can change this">
            This is what {org.name} looks like on every recording the team shares.
          </Banner>
        ) : null}

        <Section
          title="Logos"
          description="The landscape logo stands where the Slate wordmark does, at the top of every share page. The square one is used anywhere small: the browser tab, and the preview when a link is pasted into a chat."
        >
          <div className="flex flex-wrap items-start gap-8">
            <LogoSlot
              kind="wide"
              url={branding.wideLogoUrl}
              disabled={locked}
              busy={logo.isPending && logo.variables?.kind === 'wide'}
              onPick={(file) => logo.mutate({ kind: 'wide', file })}
              onClear={() => clearLogo.mutate('wide')}
            />
            <LogoSlot
              kind="square"
              url={branding.squareLogoUrl}
              disabled={locked}
              busy={logo.isPending && logo.variables?.kind === 'square'}
              onPick={(file) => logo.mutate({ kind: 'square', file })}
              onClear={() => clearLogo.mutate('square')}
            />
          </div>
          {logo.isError ? (
            <div className="mt-3">
              <ErrorText>{errorMessage(logo.error)}</ErrorText>
            </div>
          ) : null}
        </Section>

        <Section
          title="Brand colour"
          description="Used for the player's progress bar, buttons and highlights on your share pages, and for the header and button in emails the team sends."
        >
          <form
            className="flex flex-wrap items-end gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              if (colorValid || color.trim() === '') save.mutate({ brandColor: color.trim() || null });
            }}
          >
            <label className="flex h-9 w-12 shrink-0 cursor-pointer items-center justify-center rounded border border-hairline bg-slate-900">
              <input
                type="color"
                value={colorValid ? (color.startsWith('#') ? color : `#${color}`) : '#1a6e64'}
                disabled={locked}
                onChange={(event) => setColor(event.target.value)}
                className="h-7 w-9 cursor-pointer border-0 bg-transparent p-0"
                aria-label="Pick a brand colour"
              />
            </label>
            <div className="w-40">
              <Input
                label="Hex"
                value={color}
                placeholder="#1a6e64"
                disabled={locked}
                spellCheck={false}
                maxLength={7}
                onChange={(event) => setColor(event.target.value)}
                error={color.trim() && !colorValid ? 'Looks like #1a6e64.' : null}
              />
            </div>
            <Button
              type="submit"
              variant="primary"
              size="sm"
              busy={save.isPending}
              disabled={locked || !colorDirty || (color.trim() !== '' && !colorValid)}
            >
              Save
            </Button>
            {branding.brandColor ? (
              <button
                type="button"
                className="pb-2 text-caption text-chalk-dim hover:text-chalk disabled:opacity-40"
                disabled={locked || save.isPending}
                onClick={() => save.mutate({ brandColor: null })}
              >
                Clear
              </button>
            ) : null}
          </form>
          {branding.brandColor ? (
            <div className="mt-4 flex items-center gap-3">
              <span
                className="inline-flex h-8 items-center rounded px-3 text-caption font-medium"
                style={{ background: branding.brandColor, color: branding.brandInk ?? '#fff' }}
              >
                A button in your colour
              </span>
              <span className="text-caption text-chalk-faint">
                Text is set to {branding.brandInk === '#000000' ? 'black' : 'white'} so it stays readable.
              </span>
            </div>
          ) : null}
          {save.isError ? (
            <div className="mt-3">
              <ErrorText>{errorMessage(save.error)}</ErrorText>
            </div>
          ) : null}
        </Section>

        <Section
          title="Intro and outro"
          description="Recordings from the team library, played around every recording the team shares. They apply to everything already recorded, and changing one takes effect straight away."
        >
          <div className="space-y-3">
            <SlatePicker
              label="Intro"
              description="Plays before the recording. Viewers can skip it."
              current={branding.intro}
              choices={slateChoices}
              loading={library.isLoading}
              disabled={locked}
              onChoose={(introVideoId) => save.mutate({ introVideoId })}
              onClear={() => save.mutate({ introVideoId: null })}
            />
            <SlatePicker
              label="Outro"
              description="Plays once the recording finishes, before the end card."
              current={branding.outro}
              choices={slateChoices}
              loading={library.isLoading}
              disabled={locked}
              onChoose={(outroVideoId) => save.mutate({ outroVideoId })}
              onClear={() => save.mutate({ outroVideoId: null })}
            />
          </div>
          <p className="mt-3 text-caption text-chalk-faint">
            Slates are played by the share page, so a downloaded MP4 is still just the recording.
          </p>
        </Section>

        <Section
          title="Custom domain"
          description="Serve your links from your own address, so a recording you send reads as yours from the link onwards."
        >
          <DomainSection branding={branding} orgId={org.id} disabled={locked} onSettled={settled} />
        </Section>
      </div>
    </>
  );
}
